import React from 'react';
import { Box, Typography, Card, CardContent, Stack } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSuitcaseRolling, faSuitcase } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { AlTextField } from '../../../common/components/AlTextField';
import { InputRow } from '../BookingTransferForm/InputRow';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import { getServiceTypeById } from '../../models/service-type';
import css from './BookingDetailsForm.module.css';

// Dynamic pricing for HL and ADD extras is now fetched from the database
// via the DictionaryService.getLuggages() API endpoint

const LuggageSelection = ({ transfer, updateTransfer, bookingError = () => false, sx = {} }) => {
    const { t } = useTranslation();
    const { luggages = [] } = useDictionary();

    // Calculate total number of persons
    const totalPersons = (transfer.adults || 0) + (transfer.children || 0) + (transfer.kids || 0);

    // Calculate free luggage allowances based on person count
    const freeHandLuggage = totalPersons; // 1 free HL per person
    const freeSuitcases = totalPersons * 2; // 2 free ADD per person

    // Get dynamic prices from luggages data
    const handLuggageItem = luggages.find((luggage) => luggage.internalCode === 'HL');
    const suitcaseItem = luggages.find((luggage) => luggage.internalCode === 'ADD');

    const serviceType = getServiceTypeById(transfer.serviceTypeId);
    const handLuggagePrice = handLuggageItem ? handLuggageItem[serviceType] || 0 : 0;
    const suitcasePrice = suitcaseItem ? suitcaseItem[serviceType] || 0 : 9;

    const handleHandLuggageChange = ({ target }) => {
        const { value } = target;

        // Allow empty input to clear the field; do not delete when value is '0'
        if (value === '') {
            const newExtras = { ...transfer.extras };
            delete newExtras.HL;
            updateTransfer({ value: newExtras, name: 'extras' });
            return;
        }

        const numericValue = Math.max(0, parseInt(value, 10) || 0);
        updateTransfer({ name: 'extras.HL', value: numericValue });
    };

    const handleSuitcaseChange = ({ target }) => {
        const { value } = target;

        // Allow empty input to clear the field; keep 0 as a valid value
        if (value === '') {
            const newExtras = { ...transfer.extras };
            delete newExtras.ADD;
            updateTransfer({ value: newExtras, name: 'extras' });

            // Set api flag onlyHandLuggage to true
            newExtras.onlyHandLuggage = true;

            return;
        }

        if (value === '0') {
            const newExtras = { ...transfer.extras };
            newExtras.onlyHandLuggage = true;
        }

        const numericValue = Math.max(0, parseInt(value, 10) || 0);
        updateTransfer({ name: 'extras.ADD', value: numericValue });
    };

    // Use nullish coalescing so that 0 remains visible and '' shows placeholder
    const handLuggageCount = transfer.extras?.HL ?? '';
    const suitcaseCount = transfer.extras?.ADD ?? '';

    // Calculate excess luggage and pricing
    const excessHandLuggage = Math.max(0, (handLuggageCount || 0) - freeHandLuggage);
    const excessSuitcases = Math.max(0, (suitcaseCount || 0) - freeSuitcases);

    const handLuggageCost = excessHandLuggage * handLuggagePrice;
    const suitcaseCost = excessSuitcases * suitcasePrice;

    return (
        <Box sx={{ ...sx }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                {t('luggageSelection.title')}
            </Typography>

            {/* Free Luggage Allowance Card */}
            <Card
                sx={{
                    mb: 3,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid grey.300',
                    boxShadow: 'none',
                }}
            >
                <CardContent sx={{ p: 2 }}>
                    <Typography variant="body" component="p" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                        {t('luggageSelection.includedTitle')}
                    </Typography>
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FontAwesomeIcon icon={faSuitcase} style={{ fontSize: '16px', color: '#666' }} />
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {t('luggageSelection.handLuggageItem')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FontAwesomeIcon icon={faSuitcaseRolling} style={{ fontSize: '22px', color: '#666' }} />
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {t('luggageSelection.suitcaseItem')}
                            </Typography>
                        </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                        {t('luggageSelection.additionalCost', { cost: suitcasePrice })}
                    </Typography>
                </CardContent>
            </Card>

            {/* Luggage Selection Inputs */}
            <div className={css.row}>
                <div className={css.item}>
                    <InputRow icon={faSuitcase} iconFontSize="18px">
                        <AlTextField
                            fullWidth
                            name="extras.HL"
                            type="number"
                            slotProps={{ htmlInput: { min: 0 } }}
                            onChange={handleHandLuggageChange}
                            value={handLuggageCount}
                            placeholder={t('luggageSelection.handLuggage')}
                            required={true}
                            error={bookingError('transfers.0.extras.HL')}
                        />
                    </InputRow>
                    {excessHandLuggage > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {t('luggageSelection.extraCostHandLuggage', {
                                count: excessHandLuggage,
                                cost: handLuggageCost,
                            })}
                        </Typography>
                    )}
                </div>
                <div className={css.item}>
                    <InputRow icon={faSuitcaseRolling}>
                        <AlTextField
                            fullWidth
                            name="extras.ADD"
                            type="number"
                            slotProps={{ htmlInput: { min: 0 } }}
                            onChange={handleSuitcaseChange}
                            value={suitcaseCount}
                            placeholder={t('luggageSelection.suitcase')}
                            required={true}
                            error={bookingError('transfers.0.extras.ADD')}
                        />
                    </InputRow>
                    {excessSuitcases > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {t('luggageSelection.extraCostSuitcase', {
                                count: excessSuitcases,
                                cost: suitcaseCost,
                            })}
                        </Typography>
                    )}
                </div>
            </div>
        </Box>
    );
};

export { LuggageSelection };
